import React from 'react';
import { Box, Skeleton, Typography } from '@mui/material';
import { PieChart } from '@mui/x-charts/PieChart';

// Shared building blocks for the Clients-page metric dialogs (Client Metrics, Service
// Statistics): white hairline-bordered panels with an uppercase 11px header, green-accent stat
// tiles, and a donut + sorted legend breakdown. Same visual language as the DetailSection cards
// in an expanded client row.

export const METRICS_ACCENT = '#1D9E75';

// Categorical colors for labels that vary per clinic/roster (housing, counselors, service types).
export const CATEGORICAL_PALETTE = [
  '#3F51B5', '#009688', '#FF9800', '#9C27B0', '#00BCD4', '#8BC34A',
  '#E91E63', '#795548', '#607D8B', '#FFC107', '#4CAF50', '#F44336',
];
export const buildRotatingColorMap = (labels) => {
  const map = {};
  labels.forEach((label, i) => { map[label] = CATEGORICAL_PALETTE[i % CATEGORICAL_PALETTE.length]; });
  return map;
};

export const MetricPanel = ({ icon: Icon, title, subtitle, children, sx }) => (
  <Box
    sx={{
      bgcolor: '#fff', border: '1px solid rgba(0,0,0,0.08)', borderRadius: 3,
      p: 2, display: 'flex', flexDirection: 'column', minWidth: 0, ...sx,
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
        {Icon && <Icon sx={{ fontSize: 16, color: 'text.secondary' }} />}
        <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'text.secondary', whiteSpace: 'nowrap' }}>
          {title}
        </Typography>
      </Box>
      {subtitle && (
        <Typography sx={{ fontSize: 11, color: 'text.secondary', whiteSpace: 'nowrap' }}>{subtitle}</Typography>
      )}
    </Box>
    {children}
  </Box>
);

// One figure with an icon and an optional caption line (e.g. "38% of clients").
export const StatTile = ({ label, value, caption, icon: Icon, isLoading, color = METRICS_ACCENT }) => (
  <Box
    sx={{
      flex: '1 1 150px', minWidth: 150,
      display: 'flex', alignItems: 'center', gap: 1.25,
      bgcolor: 'rgba(29,158,117,0.05)', border: '1px solid rgba(29,158,117,0.18)',
      borderRadius: 2.5, px: 1.5, py: 1.25,
    }}
  >
    {isLoading ? (
      <Skeleton variant="circular" width={44} height={44} sx={{ flexShrink: 0 }} />
    ) : (
      <Box
        sx={{
          width: 44, height: 44, flexShrink: 0, borderRadius: '50%',
          bgcolor: 'rgba(29,158,117,0.14)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        {Icon && <Icon sx={{ fontSize: 22, color }} />}
      </Box>
    )}
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: '#2c2a1e', lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {label}
      </Typography>
      {isLoading ? (
        <Skeleton variant="text" width={60} sx={{ fontSize: 20 }} />
      ) : (
        <>
          <Typography sx={{ fontSize: 22, fontWeight: 700, color, lineHeight: 1.15, fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>
          {caption && (
            <Typography sx={{ fontSize: 10.5, color: 'text.secondary', lineHeight: 1.2 }}>{caption}</Typography>
          )}
        </>
      )}
    </Box>
  </Box>
);

// Categorical breakdown: donut with the total in the middle plus a sorted label/value/% legend.
// The legend scrolls past ~8 rows so a long list doesn't stretch the dialog.
export const BreakdownPanel = ({ icon, title, data, colorMap = {}, isLoading }) => {
  const rows = Array.isArray(data) ? data : [];
  const total = rows.reduce((sum, item) => sum + (item.value || 0), 0);
  const chartData = rows.map((item, index) => ({
    id: index,
    value: item.value,
    label: item.label,
    color: colorMap[item.label] || '#c7c7c7',
  }));
  const sorted = rows.slice().sort((a, b) => (b.value || 0) - (a.value || 0));

  return (
    <MetricPanel icon={icon} title={title} subtitle={!isLoading && total > 0 ? `${total} total` : undefined}>
      {isLoading ? (
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Skeleton variant="circular" width={128} height={128} sx={{ flexShrink: 0 }} />
          <Box sx={{ flex: 1 }}>
            {[0, 1, 2, 3].map((i) => <Skeleton key={i} variant="text" sx={{ fontSize: 13 }} />)}
          </Box>
        </Box>
      ) : total === 0 ? (
        <Typography sx={{ fontSize: 13, color: 'text.secondary', py: 3, textAlign: 'center' }}>
          No data available.
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
          <Box sx={{ position: 'relative', width: 128, height: 128, flexShrink: 0 }}>
            <PieChart
              series={[{ data: chartData, innerRadius: 40, outerRadius: 62, paddingAngle: 2, cornerRadius: 3 }]}
              width={128}
              height={128}
              slots={{ legend: () => null }}
              slotProps={{ legend: { hidden: true } }}
              margin={{ top: 0, bottom: 0, left: 0, right: 0 }}
            />
            <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
              <Typography sx={{ fontSize: 22, fontWeight: 700, color: '#2c2a1e', lineHeight: 1 }}>{total}</Typography>
              <Typography sx={{ fontSize: 10, color: '#6b6a52' }}>total</Typography>
            </Box>
          </Box>
          <Box sx={{ flex: 1, minWidth: 0, maxHeight: 232, overflowY: 'auto', pr: 0.5 }}>
            {sorted.map((item) => {
              const pct = total > 0 ? Math.round((item.value / total) * 100) : 0;
              return (
                <Box
                  key={item.label}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1, py: 0.6,
                    borderBottom: '1px solid #F0F0F0',
                    '&:last-child': { borderBottom: 'none' },
                  }}
                >
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: colorMap[item.label] || '#c7c7c7', flexShrink: 0 }} />
                  <Typography sx={{ fontSize: 12.5, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {item.label}
                  </Typography>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: 'text.secondary', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                    {item.value} ({pct}%)
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
    </MetricPanel>
  );
};
